import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { selectTemplateEditCreate } from 'store/KnowledgeBase/selectors';
import { resetState, createTemplate } from 'Screens/Contextualization/KnowledgeBaseEditCreate/actions/Actions';
import ModalWrapper from 'Common/Components/ModalWrapper';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import useModalShortcuts from 'Hooks/useModalShortcuts';
import useTabsShortcuts from 'Hooks/useTabsShortcuts';
import General from './components/General';
import TechnicalDetails from './components/TechnicalDetails';
import {
  Wrapper, Title, ErrorMessage, CancelButton, CreateButton, Header, ButtonWrapper, ModalBody, ModalIcon, Tabs, Tab, NextIcon
} from './styled';
import CustomAttributes from './components/CustomAttributes';
import CreationWarning from 'Common/Components/CreationWarning';
import { useIntl } from 'react-intl';

const TABS = [
  { id: 'general' },
  { id: 'technicalDetails' },
  { id: 'customAttributes' }
];

const ManualCreationModal = ({ handleClose, show }) => {
  const {
    errorMessage, exploitation, id, name
  } = useSelector(selectTemplateEditCreate);
  const [activeTab, setActiveTab] = useState('general');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const dispatch = useDispatch();
  const intl = useIntl();
  const createWarningMessage = intl.formatMessage({ id: 'modal.createVuln.cantCreate.message' });

  const setFieldError = (fieldName, hasError) => {
    setFieldErrors((errors) => ({
      ...errors,
      [fieldName]: hasError
    }));
  };

  const onHandleClose = () => {
    handleClose();
    setActiveTab('general');
    setFieldErrors({});
    dispatch(resetState());
  };

  const canCreate = !isEmpty(name) && !isEmpty(exploitation);
  const hasValidationErrors = Object.values(fieldErrors).some(Boolean);

  const onHandleSubmitClose = async () => {
    if (hasValidationErrors) return;

    if (canCreate) {
      dispatch(createTemplate());
      onHandleClose();
    } else setShowErrorModal(true);
  };

  const RenderTab = {
    general: <General setFieldError={ setFieldError } />,
    technicalDetails: <TechnicalDetails />,
    customAttributes: <CustomAttributes />
  };

  const shortcutScope = useModalShortcuts({
    id: 'kb-template-create',
    visible: show,
    onSave: onHandleSubmitClose,
    onCancel: onHandleClose,
    canSave: !hasValidationErrors,
    saveLabel: id ? 'Save' : 'Create'
  });

  useTabsShortcuts({ tabs: TABS, activeId: activeTab, onChange: setActiveTab, scope: shortcutScope });

  return show && (
  <ModalWrapper>
    <Wrapper data-testid="kb-modal">
      <Header>
        <Title>{ id > 0 ? 'Edit Vulnerability Template' : 'Create New Vulnerability Template'}</Title>
        <ButtonWrapper>
          <CancelButton onClick={ onHandleClose } data-testid="kb-modal-cancel">Cancel</CancelButton>
          <CreateButton onClick={ onHandleSubmitClose } disabled={ hasValidationErrors } data-testid="kb-modal-submit">
            <span>{ id ? 'Save' : 'Create'}</span>
            <NextIcon />
          </CreateButton>
          <ModalIcon />
        </ButtonWrapper>
      </Header>
      <ModalBody>
        <Tabs>
          <Tab onClick={ () => setActiveTab('general') } active={ activeTab === 'general' } data-testid="kb-modal-tab-general">General</Tab>
          <Tab onClick={ () => setActiveTab('technicalDetails') } active={ activeTab === 'technicalDetails' } data-testid="kb-modal-tab-technical-details">Technical Details</Tab>
          <Tab onClick={ () => setActiveTab('customAttributes') } active={ activeTab === 'customAttributes' } data-testid="kb-modal-tab-custom-attributes">Custom Attributes</Tab>
        </Tabs>
        {RenderTab[activeTab]}
      </ModalBody>
      {errorMessage.length > 0 ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
    </Wrapper>
    { showErrorModal && <CreationWarning errorTitle="Create New Vulnerability Template" errorMessage={ createWarningMessage } onClose={ () => setShowErrorModal(false) } /> }
  </ModalWrapper>
  );
};

ManualCreationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired
};

export default ManualCreationModal;
